import { useState, useCallback } from 'react';
import api from '../services/api';
import type { SEOAnalysisResponse, SEOCheck, SEOCheckStatus } from '../types';

interface SEOPanelProps {
  keyword: string;
  title?: string;
  description?: string;
  content?: string;
  slug?: string;
}

const STATUS_CONFIG: Record<SEOCheckStatus, { icon: string; color: string; bgColor: string }> = {
  good: { icon: '✓', color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-100 dark:bg-green-900/40' },
  warning: { icon: '!', color: 'text-yellow-700 dark:text-yellow-300', bgColor: 'bg-yellow-100 dark:bg-yellow-900/40' },
  problem: { icon: '✕', color: 'text-red-700 dark:text-red-300', bgColor: 'bg-red-100 dark:bg-red-900/40' },
  not_applicable: { icon: '-', color: 'text-gray-500 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-700' },
};

const PRIORITY_LABELS: Record<number, string> = {
  1: 'High Priority',
  2: 'Medium Priority',
  3: 'Low Priority',
};

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600 dark:text-green-400';
  if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
  if (score >= 40) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}

function getScoreRingColor(score: number): string {
  if (score >= 80) return 'stroke-green-500';
  if (score >= 60) return 'stroke-yellow-500';
  if (score >= 40) return 'stroke-orange-500';
  return 'stroke-red-500';
}

function ScoreCircle({ score }: { score: number }) {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="relative w-24 h-24">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="8"
        />
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          className={getScoreRingColor(score)}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-2xl font-bold ${getScoreColor(score)}`}>{score}</span>
      </div>
    </div>
  );
}

function CheckItem({ check }: { check: SEOCheck }) {
  const config = STATUS_CONFIG[check.status];

  return (
    <div className="flex items-start gap-3 py-2">
      <div className={`flex-shrink-0 w-6 h-6 rounded-full ${config.bgColor} flex items-center justify-center`}>
        <span className={`text-xs font-bold ${config.color}`}>{config.icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{check.title}</div>
        <div className="text-sm text-gray-600 dark:text-gray-300">{check.message}</div>
      </div>
    </div>
  );
}

export default function SEOPanel({ keyword, title, description, content, slug }: SEOPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<SEOAnalysisResponse | null>(null);

  const runAnalysis = useCallback(async () => {
    if (!keyword) {
      setError('No keyword available for analysis');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await api.analyzeSEO({
        keyword,
        title: title || undefined,
        description: description || undefined,
        content: content || undefined,
        slug: slug || undefined,
      });
      setAnalysis(result);
      setExpanded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze SEO');
    } finally {
      setLoading(false);
    }
  }, [keyword, title, description, content, slug]);

  // Group checks by priority
  const groupedChecks = analysis?.checks.reduce<Record<number, SEOCheck[]>>((acc, check) => {
    if (!acc[check.priority]) acc[check.priority] = [];
    acc[check.priority].push(check);
    return acc;
  }, {}) || {};

  // Count by status
  const statusCounts = analysis?.checks.reduce<Record<SEOCheckStatus, number>>(
    (acc, check) => {
      acc[check.status] = (acc[check.status] || 0) + 1;
      return acc;
    },
    { good: 0, warning: 0, problem: 0, not_applicable: 0 }
  );

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => analysis && setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          disabled={!analysis}
        >
          {analysis && (
            <svg
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
          SEO Analysis
          {analysis && (
            <span className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
              analysis.overall_status === 'good' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
              analysis.overall_status === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' :
              'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
            }`}>
              Score: {analysis.overall_score}
            </span>
          )}
        </button>
        <button
          onClick={runAnalysis}
          disabled={loading || !keyword}
          className="px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Analyzing...' : analysis ? 'Re-analyze' : 'Check SEO'}
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</div>
      )}

      {expanded && analysis && (
        <div className="mt-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          {/* Score Overview */}
          <div className="flex items-center gap-6 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <ScoreCircle score={analysis.overall_score} />
            <div className="flex-1">
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">{analysis.summary}</div>
              <div className="flex gap-4 text-xs">
                {statusCounts && statusCounts.good > 0 && (
                  <span className="text-green-600 dark:text-green-400">{statusCounts.good} passed</span>
                )}
                {statusCounts && statusCounts.warning > 0 && (
                  <span className="text-yellow-600 dark:text-yellow-400">{statusCounts.warning} warnings</span>
                )}
                {statusCounts && statusCounts.problem > 0 && (
                  <span className="text-red-600 dark:text-red-400">{statusCounts.problem} problems</span>
                )}
              </div>
            </div>
          </div>

          {/* Grouped Checks */}
          <div className="space-y-4">
            {[1, 2, 3].map(priority => {
              const checks = groupedChecks[priority];
              if (!checks || checks.length === 0) return null;

              // Filter out not_applicable checks for cleaner display
              const relevantChecks = checks.filter(c => c.status !== 'not_applicable');
              if (relevantChecks.length === 0) return null;

              return (
                <div key={priority}>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                    {PRIORITY_LABELS[priority]}
                  </div>
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {relevantChecks.map(check => (
                      <CheckItem key={check.id} check={check} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!analysis && !loading && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Click "Check SEO" to analyze how well optimized your content is for the target keyword.
        </p>
      )}
    </div>
  );
}
